import React, { useCallback, useEffect, useState } from "react";
import { IonButton, IonModal, IonSpinner } from "@ionic/react";
import { acceptConsents, fetchOutstandingConsents } from "../../services/auth";
import useAuth from "../../hooks/useAuth";

/**
 * ConsentGate — blocks the app until the kernel's required documents are accepted.
 *
 * ⚠️ THIS IS NOT THE ENFORCEMENT. The kernel enforces consent server-side: `get_current_user`
 * (`app/api/deps.py`) is the ONLY user-resolving dependency in the kernel — 93 routes hang off
 * it — and it returns **403 CONSENT_REQUIRED** for every authenticated request except a
 * five-route allowlist until `profile.consents_current` is true. See ADR-0011.
 *
 * So this component exists to make that block RESOLVABLE and legible, not to impose it. If it
 * were missing, a user with outstanding consents would sign in successfully and then watch every
 * screen fail with no way to fix it — which is the actual lockout risk, not the gate itself.
 *
 * Why it can render at all: the allowlist deliberately permits exactly the calls needed to see
 * and clear the block — `GET /me`, `GET /me/consents/outstanding`, `POST /me/consents/accept`,
 * `GET /me/capabilities`, and `POST /auth/logout` so a blocked user can always leave.
 *
 * ⚠️ Deliberately NOT dismissible. There is no close affordance and no backdrop dismiss: closing
 * it would return the user to an app where every request 403s. Logging out is the escape hatch,
 * and it is allowlisted precisely so it works from here.
 */
const ConsentGate: React.FC = () => {
  const { consentsOutstanding, logout } = useAuth() as any;
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = Array.isArray(consentsOutstanding) && consentsOutstanding.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch titles/versions for display. `/me` gives only the type codes.
      setItems(await fetchOutstandingConsents());
    } catch (e) {
      console.error("consent gate: could not load outstanding documents", e);
      // Fall back to the codes from /me so the gate still renders something actionable
      // rather than an empty modal the user cannot get past.
      setItems((consentsOutstanding || []).map((type: string) => ({ type, version: "", title: type })));
      setError("Could not load the latest documents. You can still accept to continue.");
    } finally {
      setLoading(false);
    }
  }, [consentsOutstanding]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const onAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const codes = items.map((i) => i.type).filter(Boolean);
      await acceptConsents(codes.length ? codes : consentsOutstanding);
      // The kernel flips `consents_current` in the same transaction. Re-read identity so the
      // gate closes from the SERVER's answer rather than from an optimistic local guess —
      // if acceptance did not actually stick, the gate must stay up.
      window.dispatchEvent(new CustomEvent("auth:consents-accepted"));
    } catch (e: any) {
      console.error("consent gate: accept failed", e);
      setError("Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <IonModal isOpen backdropDismiss={false} className="consent-gate-modal">
      <div className="flex flex-col h-full p-6 justify-center">
        <h2 className="text-2xl font-bold mb-2">Please review and accept</h2>
        <p className="text-base mb-4">
          We&apos;ve updated the documents below. You&apos;ll need to accept them to continue
          using Arc Connect.
        </p>

        {loading ? (
          <IonSpinner />
        ) : (
          <ul className="mb-4">
            {items.map((i) => (
              <li key={i.type} className="py-1">
                {i.title || i.type}
                {i.version ? ` (v${i.version})` : ""}
              </li>
            ))}
          </ul>
        )}

        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

        <IonButton expand="block" disabled={loading || submitting} onClick={onAccept}>
          {submitting ? "Saving…" : "I Agree"}
        </IonButton>

        {/* The only way out other than accepting. POST /auth/logout is allowlisted by the
            kernel's consent guard specifically so a blocked user is never trapped. */}
        <IonButton expand="block" fill="clear" disabled={submitting} onClick={() => logout?.()}>
          Sign out
        </IonButton>
      </div>
    </IonModal>
  );
};

export default ConsentGate;
